import { CClient, showErrorAlert } from '@common/utils'
import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import Spinner from '@components/common/spinner'
import { CompetitionCard } from '@components/profile/student/competitions'
import { ProjectCard } from '@components/profile/student/projects'
import { ProjectType } from '@lib/data/student-projects'
import {
  CodingContestType,
  HackathonType,
  ProfileDetailsType,
  ProfileSectionType,
} from '@lib/types/profile'
import {
  getAPIErrorString,
  getServerSideRedirectResult,
} from '@lib/utils/common'
import { captureException } from '@sentry/node'
import classNames from 'classnames'
import { GetServerSideProps } from 'next'
import { NextSeo } from 'next-seo'
import Link from 'next/link'
import React, { useMemo, useState } from 'react'
import { ChevronLeft } from 'react-feather'
import useInfiniteScroll from 'react-infinite-scroll-hook'

type Props = {
  nextPage: string
  username: string
  profileSection: ProfileSectionType
  items: HackathonType[] | CodingContestType[] | ProjectType[]
  profileDetails?: ProfileDetailsType
}

const ProfileSection = ({
  items: initialItems,
  nextPage: initialNextPage,
  profileSection,
  username,
  profileDetails,
}: Props) => {
  const [items, setItems] = useState<
    HackathonType[] | CodingContestType[] | ProjectType[]
  >(initialItems)
  const [nextPage, setNextPage] = useState(initialNextPage)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const handleLoadMore = async () => {
    if (isLoading || !nextPage) return
    setIsLoading(true)

    try {
      const response = await CClient(nextPage)
      if (!response.ok)
        throw new Error(await getAPIErrorString(response, nextPage))
      const data = await response.json()
      setNextPage(data?.next)
      const results = data?.results
      setItems((prevItems) => [...prevItems, ...results])
    } catch (error) {
      setHasError(true)
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong. Please refresh the page and try again or contact support for help with this issue.',
      })
    }
    setIsLoading(false)
  }

  const infiniteRef = useInfiniteScroll<HTMLDivElement>({
    loading: isLoading,
    hasNextPage: !hasError && nextPage !== null,
    onLoadMore: handleLoadMore,
  })

  const title = useMemo(() => {
    let title = ''
    switch (profileSection) {
      case 'assignments':
        title = `Coding Projects${
          profileDetails?.name ? ` by ${profileDetails?.name}` : ''
        }`
        break
      case 'projects':
        title = `Coding Activities${
          profileDetails?.name ? ` by ${profileDetails?.name}` : ''
        }`
        break
      case 'hackathons':
        title = 'Hackathons'
        break
      case 'coding-contests':
        title = 'Coding contest'
        break
    }
    return title
  }, [profileSection])

  return (
    <>
      <NextSeo title={title} />
      <NavBar />
      <div className="bg-grey-100 p-5 min-h-screen">
        <div className="max-w-6xl mx-auto grid gap-6">
          <div className="flex items-center justify-between">
            <Link href={`/@${username}/`}>
              <a className="text-orange hover:underline focus:outline-none font-600 flex items-center">
                <ChevronLeft className="hidden lg:block" />
                <span>Go back</span>
              </a>
            </Link>
          </div>
          <div className="flex flex-col p-5 lg:p-10 bg-white rounded-lg text-grey items-start">
            <h1 className="text-2xl font-600 text-grey max-w-2xl mb-4">
              {title}
            </h1>
            <div
              ref={infiniteRef}
              className={classNames('grid gap-6 w-full', {
                'lg:grid-cols-2':
                  profileSection === 'hackathons' ||
                  profileSection === 'coding-contests',
                'lg:grid-cols-3':
                  profileSection === 'assignments' ||
                  profileSection === 'projects',
              })}
            >
              {items.map((item) => {
                if (
                  profileSection === 'hackathons' ||
                  profileSection === 'coding-contests'
                )
                  return (
                    <CompetitionCard
                      key={item.slug}
                      competition={item}
                      competitionType={profileSection as any}
                    />
                  )
                if (
                  profileSection === 'assignments' ||
                  profileSection === 'projects'
                )
                  return <ProjectCard key={item.submittedAt} project={item} />
              })}
            </div>
            {isLoading && (
              <div className="flex justify-center py-20 w-full">
                <Spinner classes="text-orange h-10 w-10" />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const slug = ctx.params?.slug as string
  const profileSection = ctx.params?.profileSection as string

  const username = slug.slice(1)

  const profileResponse = await CClient(`/api/v1/profiles/${username}/`)

  if (profileResponse.status === 404) return { props: {}, notFound: true }
  if (!profileResponse.ok)
    return getServerSideRedirectResult(ctx, `/${profileResponse.status}`)

  const profileDetails: ProfileDetailsType = await profileResponse.json()

  const response = await CClient(
    `/api/v1/profiles/${username}/${profileSection}/?page_size=12`
  )

  if (response.status === 404) return { notFound: true, props: {} }
  if (!response.ok)
    return getServerSideRedirectResult(ctx, `/${response.status}`)

  const data = await response.json()

  const serverEndpoint = process.env.SSR_API_ENDPOINT || ''
  const clientEndpoint = process.env.CSR_API_ENDPOINT || ''
  const nextPage =
    data?.next && serverEndpoint && clientEndpoint
      ? (data.next as string).replace(serverEndpoint, clientEndpoint)
      : data?.next
  const items = data?.results

  if (items?.length === 0) return { props: {}, notFound: true }

  return {
    props: { nextPage, username, profileSection, items, profileDetails },
  }
}

export default ProfileSection
